/**
 * Erfassungsquelle „ninox" (Fork-Linie): Ninox im Fremd-Tab.
 *
 * Ninox ist nicht einbettbar (CSP frame-ancestors) — die Anwendung öffnet
 * per Deep-Link in einem separaten Tab (openApp()-Strategie, Live/DEV-Wahl).
 * Mechanik (Tab-Freigabe, Live-Vorschau, Screenshot) liefert die gemeinsame
 * FremdTabSource; hier nur Ninox-Texte und der Einmal-Hinweis auf den
 * eigenen Ninox-Login (kein SSO).
 */

import { useState } from "react"
import { openApp, type AppZiel } from "@/components/AppSwitcher"
import { NinoxTabCapture } from "@/lib/screenshot"
import type { Application } from "@/types"
import { FremdTabSource, type FremdTabTexte } from "./FremdTabSource"

const LOGIN_HINWEIS_KEY = "ninoxLoginHinweisGesehen"

const TEXTE: FremdTabTexte = {
  intro:
    "Deine Ninox-Anwendung läuft in einem eigenen Browser-Tab — sie kann hier nicht eingebettet werden. Öffne sie, arbeite dort wie gewohnt und komm zum Melden hierher zurück.",
  tab: "Ninox-Tab",
  oeffnen: { live: "Ninox Live öffnen", dev: "Ninox DEV öffnen", einzig: "In Ninox öffnen" },
  zieleHinweis: (
    <>
      <span className="font-semibold">Live</span> = echte Daten,{" "}
      <span className="font-semibold">DEV</span> = Testdaten. Die Struktur ist in beiden
      identisch.
    </>
  ),
  schritt1: (
    <>
      <span className="font-semibold text-foreground">In Ninox öffnen</span> und dort zur Stelle
      navigieren, um die es geht (Tabelle, Ansicht, Datensatz).
    </>
  ),
  fehlerVerbinden: "Ninox-Tab konnte nicht verbunden werden",
  fehlerVerloren: "Verbindung zum Ninox-Tab verloren — bitte neu verbinden",
}

export interface NinoxTabSourceProps {
  anwendung: Application
  /** Bereitschafts-Gate: gesetzt = Melden gesperrt (nxtools analysiert noch) */
  meldenGesperrtGrund?: string
  /** Aufgenommenes Bild — der Kern legt damit die Meldung an (#108) */
  onBild: (bild: Blob) => void
  onDateiWaehlen: () => void
  /** Kern arbeitet gerade (Meldung anlegen + Upload) */
  beschaeftigt?: boolean
  /** Screenshot-Umgebung (Δ Ninox): live = echte Daten, dev = Testdaten —
   * fließt im Kern in den Report-Kontext (F8) ein */
  onUmgebungChange: (umgebung: AppZiel) => void
}

export function NinoxTabSource({
  anwendung,
  onUmgebungChange,
  meldenGesperrtGrund,
  ...rest
}: NinoxTabSourceProps) {
  // F2a — Einmal-Hinweis: Ninox hat einen eigenen Login (kein SSO)
  const [loginHinweis, setLoginHinweis] = useState(
    () => localStorage.getItem(LOGIN_HINWEIS_KEY) !== "1"
  )
  const loginHinweisSchliessen = () => {
    localStorage.setItem(LOGIN_HINWEIS_KEY, "1")
    setLoginHinweis(false)
  }

  // openApp()-Strategie (SCHNITTLISTE §5): ninox ⇒ Deep-Link im Fremd-Tab,
  // mit Live/DEV-Wahl. Die tatsächlich geöffnete Umgebung geht an den Kern.
  const inNinoxOeffnen = (ziel: AppZiel) => {
    const oeffnung = openApp(anwendung, ziel)
    if (oeffnung.modus === "tab") onUmgebungChange(oeffnung.umgebung)
  }

  return (
    <FremdTabSource
      anwendung={anwendung}
      oeffnen={inNinoxOeffnen}
      texte={TEXTE}
      captureErzeugen={() => new NinoxTabCapture()}
      hinweis={
        loginHinweis && (
          <div className="flex max-w-xl items-start gap-3 rounded-[10px] border border-warning/30 bg-warning/10 px-4 py-3 text-left text-[13px]">
            <span className="min-w-0">
              <span className="font-semibold">Hinweis:</span> Ninox hat einen{" "}
              <span className="font-semibold">eigenen Login</span> — melde dich dort mit deinem
              Ninox-Konto an. Deine Anmeldung hier im Portal gilt dort nicht.
            </span>
            <button
              type="button"
              onClick={loginHinweisSchliessen}
              className="shrink-0 rounded bg-secondary px-2 py-1 text-[11px] font-bold hover:bg-secondary/80"
            >
              Verstanden
            </button>
          </div>
        )
      }
      gesperrtGrund={meldenGesperrtGrund}
      {...rest}
    />
  )
}
